import { message } from "antd";
import { type FormikErrors } from "formik";
import { isEmpty } from "lodash-es";
import { Component } from "react";
import { type RouteComponentProps } from "react-router";

import type { CalendarFeedItemViewModel } from "@btnet/services-btnet/handlers/models/calendarFeedItemViewModel";
import { IDTypes } from "@btnet/services-btnet/handlers/models/iDTypes";
import {
    schedulesCalendarFeedPostV2,
    schedulesCalendarFeedPutV2,
} from "@btnet/services-btnet/handlers/schedules/schedules";

import { AppContext } from "helpers/AppProvider";

import { AccountingIntegrationType } from "types/enum";

import { type ITrackingProp, track } from "utilities/analytics/analytics";
import { BadRequestError, showAPIErrorMessage, UpgradeRequiredError } from "utilities/apiHandler";
import { getEditModeLink } from "utilities/editMode/edit.utility";
import { setLoadingAction } from "utilities/form/form";
import { deepClone } from "utilities/object/object";
import { routes } from "utilities/routes";

import { btDeleteConfirm, btError, btInfo } from "commonComponents/btWrappers/BTConfirm/BTConfirm";
import { BTModal, type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import {
    AccountingEntity,
    defaultAccountingSelectValue,
    UpdateAccountingResponse,
} from "commonComponents/entity/accounting/LinkedAccountingEntity/LinkedAccountingEntity.api.types";
import {
    type ILinkEntityToAccountingHandler,
    LinkEntityToAccountingHandler,
} from "commonComponents/entity/accounting/LinkEntityToAccounting/LinkEntityToAccounting.api.handler";
import { SmsOptInStatus } from "commonComponents/entity/cellPhone/CellPhoneInput/CellPhoneInput.types";
import {
    type IJobAccess,
    type JobAccessEntity,
} from "commonComponents/entity/jobAccess/JobAccess/JobAccess.api.types";
import { withErrorBoundary } from "commonComponents/helpers/ErrorBoundary/ErrorBoundary";
import { withUpgradeRequiredBoundary } from "commonComponents/helpers/UpgradeRequiredBoundary/UpgradeRequiredBoundary";
import { BTLoading } from "commonComponents/utilities/BTLoading/BTLoading";
import {
    deleteSearchEntity,
    SearchCategory,
} from "commonComponents/utilities/MainNavigation/searchLegacy/SearchBar.api.types";

import { FilterHandler, type IFilterHandler } from "entity/filters/Filter/Filter.api.handler";
import {
    type IInternalUserDeleteConflictsFormValues,
    type InternalUserDeleteConflictsEntity,
} from "entity/internalUser/InternalUserDeleteConflicts/InternalUserDeleteConflicts.api.types";
import "entity/internalUser/InternalUserDetails/InternalUserDetails.less";
import { JobHandler } from "entity/job/Job.api.handler";
import { getTakeoffErrorContentFromCode } from "entity/takeoff/takeoff.utility";
import {
    type ITakeoffSettingsHandler,
    TakeoffSettingsHandler,
} from "entity/takeoff/TakeoffSettings/TakeoffSettings.api.handler";
import { VendorTypes } from "entity/vendor/common/vendor.types";
import { getErrorContentFromCode } from "entity/vendor/common/vendor.utility";
import {
    HomeDepotSettingsHandler,
    type IHomeDepotSettingsHandler,
} from "entity/vendor/HomeDepotSettings/HomeDepotSettings.api.handler";

import { type IInternalUserHandler, InternalUserHandler } from "./InternalUser.api.handler";
import {
    FeedType,
    type IInternalUserFormValues,
    InternalUserEntity,
    InternalUserEntitySaveRequest,
    type InternalUserFormActions,
    type InternalUserLoadAction,
    InternalUserTabKeys,
} from "./InternalUser.api.types";
import { InternalUserPresentational } from "./InternalUserPresentational";

interface IUrlProps {}

export interface IInternalUserProps extends RouteComponentProps<IUrlProps> {
    id: number;
    initialTab?: InternalUserTabKeys;
    internalUserLoadAction?: InternalUserLoadAction;
    handler?: IInternalUserHandler;
    filterHandler?: IFilterHandler;
    homeDepotSettingsHandler?: IHomeDepotSettingsHandler;
    takeoffSettingsHandler?: ITakeoffSettingsHandler;
    accountingHandler?: ILinkEntityToAccountingHandler;
    modalConfig?: IModalConfiguration;
    thdConnected?: boolean;
    error?: string;
    onSave?: (internalUserId: number) => void;
    onAddInternalUserToViewingAccess?: (
        id: number,
        fullName: string,
        jobStatusAccess: number[],
    ) => void;
}

interface IInternalUserState {
    data?: InternalUserEntity;
    deleteConflicts?: InternalUserDeleteConflictsEntity;
    actionBeingPerformed: InternalUserFormActions;
    lastActionSaveAndNew: boolean;
    permissionWizardUserId: number;
    jobAccessIds: number[];
    shouldRenderAddUserToJobModal: boolean;
    closeAfterSave: boolean;
    jobAccess: JobAccessEntity | null;
    shouldRenderTextVerificationAlert: boolean;
}

class InternalUserInternalBase extends Component<
    IInternalUserProps & ITrackingProp,
    IInternalUserState
> {
    static defaultProps = {
        handler: new InternalUserHandler(),
        filterHandler: new FilterHandler(),
        homeDepotSettingsHandler: new HomeDepotSettingsHandler(),
        takeoffSettingsHandler: new TakeoffSettingsHandler(),
        accountingHandler: new LinkEntityToAccountingHandler(),
    };

    state: Readonly<IInternalUserState> = {
        actionBeingPerformed: undefined,
        lastActionSaveAndNew: false,
        permissionWizardUserId: 0,
        jobAccessIds: [],
        shouldRenderAddUserToJobModal: false,
        closeAfterSave: false,
        jobAccess: null,
        shouldRenderTextVerificationAlert: false,
    };

    static contextType = AppContext;
    declare context: React.ContextType<typeof AppContext>;

    componentDidMount = async () => {
        await this.loadUserSetInState();

        if (this.props.thdConnected) {
            void message.success("Connected");
        } else if (this.props.error) {
            if (this.props.initialTab && this.props.initialTab === InternalUserTabKeys.Takeoff) {
                btError(getTakeoffErrorContentFromCode(this.props.error));
            } else {
                btError(getErrorContentFromCode(VendorTypes.TheHomeDepot, this.props.error));
            }
        }
    };

    private loadUserSetInState = async () => {
        const [internalUserResponse, accountingResponse, payrollResponse] = await Promise.all([
            this.load(),
            this.loadAccounting(),
            this.loadPayrollSettings(),
        ]);

        if (internalUserResponse) {
            internalUserResponse.accountingEntity = accountingResponse;
            internalUserResponse.payrollSettingsEntity = payrollResponse;
        }

        // Only update the permissionWizardUserId if the Permission Wizard is closed
        this.setState((prevProps) => {
            return {
                data: internalUserResponse,
                permissionWizardUserId: prevProps.shouldRenderAddUserToJobModal
                    ? prevProps.permissionWizardUserId
                    : (internalUserResponse?.internalUser.id ?? 0),
            };
        });
    };

    private reloadPayrollTab = async () => {
        const [payrollResponse] = await Promise.all([this.loadPayrollSettings()]);

        const internalUserResponse = this.state.data;

        if (internalUserResponse && internalUserResponse.canView) {
            internalUserResponse.payrollSettingsEntity = payrollResponse;
        }

        this.setState({
            data: internalUserResponse,
        });
    };

    private load = async () => {
        try {
            let apiResponse;
            if (this.props.id === 0) {
                apiResponse = await this.props.handler!.getDefault();
            } else {
                apiResponse = await this.props.handler!.get(this.props.id);
            }
            return apiResponse;
        } catch (e) {
            this.setState(() => {
                throw e;
            });
            return;
        }
    };

    private getRoles = async () => {
        const apiResponse = await this.load();
        if (apiResponse) {
            return {
                roleTreeData: apiResponse.internalUser.role,
                roles: apiResponse.internalUser.roles,
            };
        }
        return { roleTreeData: [], roles: [] };
    };

    private redirectInternalUserDetails = (id: number, action: InternalUserFormActions) => {
        if (this.props.modalConfig) {
            if (action === "save") {
                this.props.history.replace(
                    this.props.modalConfig.parentRoute +
                        routes.internalUsers.getInternalUserDetailsLink(
                            id,
                            InternalUserTabKeys.Overview,
                        ),
                );
            } else if (action === "saveAndNew") {
                this.props.history.replace(
                    getEditModeLink(
                        this.props.modalConfig.parentRoute +
                            routes.internalUsers.getInternalUserDetailsLink(
                                id,
                                InternalUserTabKeys.Overview,
                            ),
                    ),
                );
            }
        }
    };

    private loadAccounting = async () => {
        try {
            const { id } = this.props;
            if (id !== 0) {
                const apiResponse = await this.props.handler!.getAccountingInfo(id);
                if (apiResponse.canViewAccountingTab) {
                    return new AccountingEntity(id, apiResponse);
                }
                return undefined;
            } else {
                return undefined;
            }
        } catch (e) {
            this.setState(() => {
                throw e;
            });
            return;
        }
    };

    private loadPayrollSettings = async () => {
        try {
            const { id } = this.props;
            if (id !== 0) {
                const apiResponse = await this.props.handler!.getPayrollSettingsInfo(id);
                return apiResponse;
            } else {
                return undefined;
            }
        } catch (e) {
            return undefined;
        }
    };

    private handleUpdateAccounting = async (id: number) => {
        const accountingSyncResponse = await this.props.handler!.syncEmployee(id);
        const accountingType =
            AccountingIntegrationType[
                this.state.data?.accountingEntity?.accountingIntegrationType ?? 0
            ];
        if (accountingSyncResponse.methodSuccessful && accountingSyncResponse.message === "") {
            accountingSyncResponse.message = `${accountingType} ${this.state.data?.accountingEntity?.primaryAccountingTerm} has been successfully updated!`;
        }
        if (!accountingSyncResponse.methodSuccessful && accountingSyncResponse.message === "") {
            accountingSyncResponse.message = `There was a problem updating your ${accountingType} ${this.state.data?.accountingEntity?.primaryAccountingTerm} data. Please try again in a few minutes.`;
        }
        return new UpdateAccountingResponse({
            accountingSuccess: accountingSyncResponse.methodSuccessful,
            accountingMessage: accountingSyncResponse.message,
        });
    };

    private handleUnlinkAccounting = async (id: number) => {
        return await this.props.handler!.unlinkAccountingVendor(id);
    };

    private handleLinkUserToAccounting = async (selectedAccountingEmployee: string) => {
        await setLoadingAction<InternalUserFormActions>(this, {
            actionBeingPerformed: "saveAndClose",
            callback: async () => {
                try {
                    if (selectedAccountingEmployee === defaultAccountingSelectValue.toString()) {
                        await this.props.accountingHandler!.createAccountingEntity({
                            numericEntityID: this.props.id,
                            idType: IDTypes.EmployeeID,
                        });
                    } else {
                        await this.props.accountingHandler!.saveAccountingEntity({
                            accountingEntityID: selectedAccountingEmployee,
                            numericEntityID: this.props.id,
                            idType: IDTypes.EmployeeID,
                        });
                    }
                    await this.loadUserSetInState();
                } catch (e) {
                    showAPIErrorMessage(e);
                }
            },
        });
    };

    private handleCloseAfterSave = () => {
        this.setState({ closeAfterSave: true });
    };

    private handleClose = () => {
        this.props.modalConfig?.beforeClose();
    };

    private handleSubmit = async (
        newValues: IInternalUserFormValues,
        action: InternalUserFormActions,
        setErrors: (errors: FormikErrors<IInternalUserFormValues>) => void,
    ): Promise<boolean> => {
        let success = false;
        await setLoadingAction<InternalUserFormActions>(this, {
            actionBeingPerformed: action,
            callback: async () => {
                try {
                    const response = await this.saveInternalUser(newValues);
                    if (!response) return;

                    success = this.handlePostSaveActions(response, newValues, action);
                } catch (e) {
                    this.handleSaveError(e, setErrors);
                }
            },
        });
        return success;
    };

    private async saveInternalUser(
        newValues: IInternalUserFormValues,
    ): Promise<InternalUserEntity | undefined> {
        const saveRequest = new InternalUserEntitySaveRequest(newValues, this.state.data!);
        if (this.props.id === 0) {
            return await this.props.handler!.create(saveRequest);
        } else {
            const [update, accountingResponse, payrollResponse] = await Promise.all([
                this.props.handler!.update(this.props.id, saveRequest),
                this.loadAccounting(),
                this.loadPayrollSettings(),
            ]);
            if (update) {
                update.accountingEntity = accountingResponse;
                update.payrollSettingsEntity = payrollResponse;
            }

            await this.reloadGlobalInfo(newValues);
            return update;
        }
    }

    private handlePostSaveActions(
        response: InternalUserEntity,
        newValues: IInternalUserFormValues,
        action: InternalUserFormActions,
    ) {
        this.fireToastMessage();

        if (this.props.onSave) {
            this.props.onSave(response.internalUser.id);
        }
        if (this.props.onAddInternalUserToViewingAccess && this.props.id === 0) {
            this.props.onAddInternalUserToViewingAccess(
                response.internalUser.id,
                `${newValues.firstName} ${newValues.lastName}`,
                response.internalUser.roleJobStatusAccess,
            );
        }

        if (this.props.id === 0) {
            this.redirectInternalUserDetails(response.internalUser.id, action);
        }

        const shouldRenderAddUserToJobModal = this.state.jobAccessIds.length > 0;
        const shouldRenderTextVerificationAlert =
            this.state.data?.internalUser.smsOptInStatus !== SmsOptInStatus.RequestedOptIn &&
            response.internalUser.smsOptInStatus === SmsOptInStatus.RequestedOptIn;

        this.setState({
            data: response,
            permissionWizardUserId: response.internalUser.id,
            shouldRenderAddUserToJobModal,
            jobAccess: newValues.jobAccess,
            shouldRenderTextVerificationAlert,
        });

        if (action === "saveAndClose" && shouldRenderAddUserToJobModal) {
            this.handleCloseAfterSave();
            return false;
        }
        return true;
    }

    private handleSaveError(
        e: any,
        setErrors: (errors: FormikErrors<IInternalUserFormValues>) => void,
    ) {
        if (e instanceof UpgradeRequiredError) {
            this.setState(() => {
                throw e;
            });
        } else if (e instanceof BadRequestError) {
            showAPIErrorMessage(e);
        } else if (e.response?.data?.failedFields) {
            const errors = this.parseErrors(e.response.data);
            if (!isEmpty(errors) && setErrors) {
                setErrors(errors);
            }
        } else if (e.response?.data) {
            this.setState({ data: new InternalUserEntity(e.response.data) });
        } else {
            showAPIErrorMessage(e);
        }
    }

    private reloadGlobalInfo = async (formValues: IInternalUserFormValues) => {
        if (
            this.state.data?.internalUser.isSelf &&
            this.state.data?.internalUser.role.filter((r) => r.selected)[0].id &&
            parseInt(this.state.data.internalUser.role.filter((r) => r.selected)[0].id) !==
                formValues.role
        ) {
            // If the user is changing their role, reload the global info to get the new permissions
            await this.context?.reloadGlobalInfo();
        }
    };

    private handleSaveAndNew = async (newValues: IInternalUserFormValues): Promise<boolean> => {
        return new Promise((resolve) => {
            void setLoadingAction<InternalUserFormActions>(this, {
                actionBeingPerformed: "saveAndNew",
                callback: async () => {
                    try {
                        const saveRequest = new InternalUserEntitySaveRequest(
                            newValues,
                            this.state.data!,
                        );
                        let saveResponse: InternalUserEntity;
                        if (this.props.id === 0) {
                            saveResponse = await this.props.handler!.create(saveRequest);
                        } else {
                            saveResponse = await this.props.handler!.update(
                                this.props.id,
                                saveRequest,
                            );
                        }

                        if (this.props.onSave) {
                            this.props.onSave(saveResponse.internalUser.id);
                        }

                        this.fireToastMessage();

                        if (this.props.onAddInternalUserToViewingAccess && saveResponse) {
                            this.props.onAddInternalUserToViewingAccess(
                                saveResponse.internalUser.id,
                                `${newValues.firstName} ${newValues.lastName}`,
                                saveResponse.internalUser.roleJobStatusAccess,
                            );
                        }

                        await this.reloadGlobalInfo(newValues);

                        this.redirectInternalUserDetails(0, "saveAndNew");

                        const newData = await this.load();
                        // Save the newly created user's id for the permission wizard
                        this.setState((prevState) => {
                            return {
                                data: newData,
                                permissionWizardUserId: saveResponse.internalUser.id,
                                shouldRenderAddUserToJobModal: prevState.jobAccessIds.length > 0,
                            };
                        });

                        resolve(true);
                    } catch (e) {
                        resolve(false);
                        if (e instanceof UpgradeRequiredError) {
                            this.setState(() => {
                                throw e;
                            });
                        } else {
                            showAPIErrorMessage(e);
                        }
                    }
                },
            });
        });
    };

    private handleAddUpdateCalendarFeed = async (
        id: number,
        feedType: FeedType,
        options: number[],
        hasFeed: boolean,
        setFieldValue: (field: string, value: any) => void,
        feedId?: number,
    ) => {
        let calendarResponse: CalendarFeedItemViewModel;
        if (hasFeed) {
            calendarResponse = (
                await schedulesCalendarFeedPutV2({
                    loginId: id,
                    options,
                    type: feedType,
                    calendarFeedId: feedId!,
                })
            ).data;
        } else {
            calendarResponse = (
                await schedulesCalendarFeedPostV2({ loginId: id, options, type: feedType })
            ).data;
        }

        switch (feedType) {
            case FeedType.ScheduleItems:
                setFieldValue("scheduleItemsFeed", calendarResponse);
                break;
            case FeedType.LeadActivities:
                setFieldValue("leadActivitesFeed", calendarResponse);
                break;
            case FeedType.Todos:
                setFieldValue("todosFeed", calendarResponse);
                break;
        }

        void message.success("Schedule Feed Saved!");
    };

    private handleDeleteCalendarFeed = (
        id: number,
        feedType: FeedType,
        setFieldValue: (field: string, value: any) => void,
        feedId?: number,
    ) => {
        btDeleteConfirm({
            title: "Confirm Delete",
            content:
                "If this is deleted, this link will no longer work for any users who are currently synchronizing their schedules",
            okText: "Delete",
            onOk: async () => {
                if (feedId) {
                    await this.props.handler!.deleteCalendarFeed(id, feedId);
                }

                switch (feedType) {
                    case FeedType.ScheduleItems:
                        setFieldValue("scheduleItemsFeed", "");
                        break;
                    case FeedType.LeadActivities:
                        setFieldValue("leadActivitesFeed", "");
                        break;
                    case FeedType.Todos:
                        setFieldValue("todosFeed", "");
                        break;
                }

                void message.success("Schedule Feed Deleted!");
            },
        });
    };

    private handleDelete = async () => {
        const hasConflicts = await this.handleGetDeleteConflicts();
        if (!hasConflicts) {
            btDeleteConfirm({
                entityName: "Internal User",
                onOk: async () => {
                    await setLoadingAction<InternalUserFormActions>(this, {
                        actionBeingPerformed: "delete",
                        callback: async () => {
                            try {
                                await this.props.handler!.delete(this.props.id);
                                this.handleClose();
                                deleteSearchEntity(SearchCategory.InternalUsers, this.props.id);
                            } catch (e) {
                                showAPIErrorMessage(e);
                            }
                        },
                    });
                },
            });
        }
    };

    private handleGetDeleteConflicts = async (): Promise<boolean> => {
        let hasConflicts = true;
        await setLoadingAction<InternalUserFormActions>(this, {
            actionBeingPerformed: "delete",
            callback: async () => {
                try {
                    const deleteConflicts = await this.props.handler!.getDeleteConflicts(
                        this.props.id,
                    );
                    if (deleteConflicts.hasConflicts) {
                        this.setState({ deleteConflicts });
                    }
                    hasConflicts = deleteConflicts.hasConflicts;
                } catch (e) {
                    showAPIErrorMessage(e);
                }
            },
        });
        return hasConflicts;
    };

    private handleCloseDeleteConflicts = () => {
        this.setState({ deleteConflicts: undefined });
    };

    private handleReassignJobsitesForUserDeletion = async (newLoginId: number) => {
        try {
            const oldLoginId = this.state.data!.internalUser.id;
            const deleteConflicts = await this.props.handler!.reassignJobsitesForUserDeletion(
                oldLoginId,
                newLoginId,
            );
            void message.success("Jobsites successfully reassigned.");

            if (deleteConflicts.hasConflicts) {
                this.setState({ deleteConflicts });
            } else {
                this.setState({ deleteConflicts: undefined });
            }
        } catch (e) {
            showAPIErrorMessage(e);
        }
    };

    private handleReassignItemsForUserDeletion = async (
        formValues: IInternalUserDeleteConflictsFormValues,
    ) => {
        try {
            const deleteConflicts =
                await this.props.handler!.reassignItemsForUserDeletion(formValues);
            void message.success("Items successfully reassigned.");
            if (deleteConflicts.hasConflicts) {
                this.setState({ deleteConflicts });
            } else {
                this.setState({ deleteConflicts: undefined });
            }
        } catch (e) {
            showAPIErrorMessage(e);
        }
    };

    private parseErrors = (data: { failedFields?: { key: string; message: string[] }[] }) => {
        let errors: FormikErrors<IInternalUserFormValues> = {};
        if (data.failedFields && data.failedFields.length > 0) {
            const changeSelfToNonAdminError = data.failedFields.find((x) => x.key === "role.value");
            if (changeSelfToNonAdminError) {
                btInfo({
                    content: changeSelfToNonAdminError.message[0],
                });
                errors.role = changeSelfToNonAdminError.message[0];
            }
            Array.from(data.failedFields).forEach((f: any) => {
                errors = errors || {};
                errors[f.key as keyof FormikErrors<IInternalUserFormValues>] = f.message[0];
            });
        }
        return errors;
    };

    private fireToastMessage = () => {
        void message.success("Saved Successfully");
    };

    private handleInitiateHomeDepotConnection = async () => {
        await setLoadingAction<InternalUserFormActions>(this, {
            actionBeingPerformed: "connect",
            callbackNavigates: true,
            callback: async () => {
                try {
                    const response = await this.props.homeDepotSettingsHandler!.getRedirectInfo(
                        this.props.id,
                    );
                    window.parent.location.assign(response.authorizationUrl);
                } catch (e) {
                    showAPIErrorMessage(e);
                }
            },
        });
    };

    private handleDisconnectHomeDepot = async () => {
        await setLoadingAction<InternalUserFormActions>(this, {
            actionBeingPerformed: "disconnect",
            callback: async () => {
                try {
                    await this.props.homeDepotSettingsHandler!.disconnectUser(this.props.id);
                    await this.loadUserSetInState();
                    void message.success("Disconnected");
                } catch (e) {
                    showAPIErrorMessage(e);
                }
            },
        });
    };

    private handleInitiatePartnerAccountConnection = async () => {
        await setLoadingAction<InternalUserFormActions>(this, {
            actionBeingPerformed: "connect",
            callbackNavigates: true,
            callback: async () => {
                try {
                    const response = await this.props.takeoffSettingsHandler!.getRedirectInfo(
                        this.state.data?.takeoffUserDetails?.partnerId!,
                        window.parent.location.href,
                        this.props.id,
                    );
                    window.parent.location.assign(response.authorizationUrl);
                } catch (e) {
                    showAPIErrorMessage(e);
                }
            },
        });
    };

    private handleDisconnectPartnerAccount = async () => {
        await setLoadingAction<InternalUserFormActions>(this, {
            actionBeingPerformed: "disconnect",
            callback: async () => {
                try {
                    const partnerId = this.state.data?.takeoffUserDetails?.partnerId!;
                    await this.props.takeoffSettingsHandler!.disconnectUser(
                        partnerId,
                        this.props.id,
                    );
                    await this.loadUserSetInState();
                    void message.success("Disconnected");
                } catch (e) {
                    showAPIErrorMessage(e);
                }
            },
        });
    };

    private handleJobAccessChanged = (jobAccess: IJobAccess) => {
        if (!jobAccess.hasAccess) {
            const jobCopy = deepClone(this.state.jobAccessIds);
            jobCopy.push(jobAccess.jobId);

            this.setState({ jobAccessIds: jobCopy });
        } else {
            this.setState({
                jobAccessIds: this.state.jobAccessIds.filter((j) => j !== jobAccess.jobId),
            });
        }
    };

    private handleToggleAllJobAccess = (jobAccess: IJobAccess[], checked: boolean) => {
        if (checked) {
            this.setState({ jobAccessIds: jobAccess.map((job) => job.jobId) });
        } else {
            this.setState({ jobAccessIds: [] });
        }
    };

    private handleCloseAddUsersToJobModal = () => {
        if (this.state.closeAfterSave) {
            this.handleClose();
            return;
        }
        this.setState((prevProps) => {
            return {
                shouldRenderAddUserToJobModal: false,
                permissionWizardUserId: prevProps.data?.internalUser.id ?? 0,
                jobAccessIds: [],
            };
        });
    };

    private handleDoNotUpdateUserPermissions = async () => {
        const jobHandler = new JobHandler();
        await jobHandler.updateUserPermissions(this.state.jobAccessIds, [], undefined, [
            this.state.permissionWizardUserId,
        ]);
        this.handleCloseAddUsersToJobModal();
    };

    render() {
        // Show loading spinner until entity is loaded
        if (!this.state || !this.state.data) {
            return <BTLoading />;
        }

        const routeProps = {
            history: this.props.history,
            location: this.props.location,
            match: this.props.match,
        };

        const component = (
            <InternalUserPresentational
                key={this.props.id}
                initialTab={this.props.initialTab}
                entity={this.state.data}
                handler={this.props.handler}
                filterHandler={this.props.filterHandler}
                actionBeingPerformed={this.state.actionBeingPerformed}
                onSubmit={this.handleSubmit}
                onSaveAndNew={this.handleSaveAndNew}
                onAddUpdateCalendarFeed={this.handleAddUpdateCalendarFeed}
                onDeleteCalendarFeed={this.handleDeleteCalendarFeed}
                onDelete={this.handleDelete}
                onReloadUser={this.loadUserSetInState}
                onUpdateAccounting={this.handleUpdateAccounting}
                onUnlinkAccounting={this.handleUnlinkAccounting}
                onLinkEntityToAccounting={this.handleLinkUserToAccounting}
                onReload={this.reloadPayrollTab}
                onInitiateHomeDepotConnection={this.handleInitiateHomeDepotConnection}
                onDisconnectHomeDepot={this.handleDisconnectHomeDepot}
                onInitiatePartnerAccountConnection={this.handleInitiatePartnerAccountConnection}
                onDisconnectPartnerAccount={this.handleDisconnectPartnerAccount}
                deleteConflicts={this.state.deleteConflicts}
                onCloseDeleteConflicts={this.handleCloseDeleteConflicts}
                onReassignJobsitesForUserDeletion={this.handleReassignJobsitesForUserDeletion}
                onReassignItemsForUserDeletion={this.handleReassignItemsForUserDeletion}
                lastActionSaveAndNew={this.state.lastActionSaveAndNew}
                onJobAccessChanged={this.handleJobAccessChanged}
                onToggleAllJobAccess={this.handleToggleAllJobAccess}
                jobAccess={this.state.jobAccess}
                permissionWizardUserId={this.state.permissionWizardUserId}
                jobAccessIds={this.state.jobAccessIds}
                shouldRenderAddUserToJobModal={this.state.shouldRenderAddUserToJobModal}
                onCloseAddUsersToJobModal={this.handleCloseAddUsersToJobModal}
                onDoNotUpdateUserPermissions={this.handleDoNotUpdateUserPermissions}
                modalConfig={this.props.modalConfig}
                getRoles={this.getRoles}
                shouldRenderTextVerificationAlert={this.state.shouldRenderTextVerificationAlert}
                initialEditMode={this.state.data.internalUser.isSelf}
                {...routeProps}
            />
        );

        if (this.props.modalConfig) {
            return (
                <BTModal
                    data-testid="btModalInternalUser"
                    visible
                    removeBodyPadding
                    title="Internal User"
                    className="InternalUserModal"
                    width="1200px"
                    beforeClose={this.props.modalConfig.beforeClose}
                    useModalLayout
                >
                    {component}
                </BTModal>
            );
        }
        return component;
    }
}

const InternalUserInternal = track({})(InternalUserInternalBase);

const InternalUserWithUpgradeRequired = withUpgradeRequiredBoundary(InternalUserInternal)();
export const InternalUser = withErrorBoundary(InternalUserWithUpgradeRequired)(
    "Could not load Internal User",
);
export default InternalUser;
